import React from 'react';
import { Input, Button, Select, Radio, Checkbox } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { RadioChangeEvent } from 'antd';

const PAY_TYPE_OPTIONS = [
  { value: '', label: '(全部)' },
  { value: '1', label: '1-貨款' },
  { value: '5', label: '5-預收款' },
  { value: '6', label: '6-營業外收入' },
  { value: '8', label: '8-客戶匯入款' },
  { value: '9', label: '9-還款' },
];

interface QueryParamsProps {
  shipNoFrom: string;
  shipNoTo: string;
  shipDateFrom: string;
  shipDateTo: string;
  custFrom: string;
  custTo: string;
  invDateFrom: string;
  invDateTo: string;
  payDateFrom: string;
  payDateTo: string;
  payTypeFrom: string;
  payTypeTo: string;
  docStatus: number;
  reportTypes: number[];
  loading: boolean;
  onShipNoFromChange: (v: string) => void;
  onShipNoToChange: (v: string) => void;
  onShipDateFromChange: (v: string) => void;
  onShipDateToChange: (v: string) => void;
  onCustFromChange: (v: string) => void;
  onCustToChange: (v: string) => void;
  onInvDateFromChange: (v: string) => void;
  onInvDateToChange: (v: string) => void;
  onPayDateFromChange: (v: string) => void;
  onPayDateToChange: (v: string) => void;
  onPayTypeFromChange: (v: string) => void;
  onPayTypeToChange: (v: string) => void;
  onDocStatusChange: (v: number) => void;
  onReportTypesChange: (v: number[]) => void;
  onQuery: () => void;
}

const QueryParams: React.FC<QueryParamsProps> = ({
  shipNoFrom, shipNoTo, custFrom, custTo,
  shipDateFrom, shipDateTo, invDateFrom, invDateTo,
  payDateFrom, payDateTo, payTypeFrom, payTypeTo,
  docStatus, reportTypes, loading,
  onShipNoFromChange, onShipNoToChange,
  onShipDateFromChange, onShipDateToChange,
  onCustFromChange, onCustToChange,
  onInvDateFromChange, onInvDateToChange,
  onPayDateFromChange, onPayDateToChange,
  onPayTypeFromChange, onPayTypeToChange,
  onDocStatusChange, onReportTypesChange,
  onQuery,
}) => {
  const handleReportTypeChange = (type: number, checked: boolean) => {
    if (checked) {
      onReportTypesChange([...reportTypes, type]);
    } else {
      onReportTypesChange(reportTypes.filter((t) => t !== type));
    }
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
      {/* Row 1: Ship number range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>交運單號[從]</span>
          <Input
            value={shipNoFrom}
            onChange={(e) => onShipNoFromChange(e.target.value.toUpperCase())}
            placeholder="起"
            style={{ width: 114 }}
            maxLength={7}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={shipNoTo}
            onChange={(e) => onShipNoToChange(e.target.value.toUpperCase())}
            placeholder="迄"
            style={{ width: 114 }}
            maxLength={8}
          />
        </div>
      </div>

      {/* Row 2: Ship date range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>交運日期[從]</span>
          <Input
            type="date"
            value={shipDateFrom}
            onChange={(e) => onShipDateFromChange(e.target.value)}
            style={{ width: 114 }}
          />
          <span className="text-sm">[至]</span>
          <Input
            type="date"
            value={shipDateTo}
            onChange={(e) => onShipDateToChange(e.target.value)}
            style={{ width: 114 }}
          />
        </div>
      </div>

      {/* Row 3: Customer range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>客戶代號[從]</span>
          <Input
            value={custFrom}
            onChange={(e) => onCustFromChange(e.target.value)}
            placeholder="起"
            style={{ width: 114 }}
            maxLength={12}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={custTo}
            onChange={(e) => onCustToChange(e.target.value)}
            placeholder="迄"
            style={{ width: 114 }}
            maxLength={12}
          />
        </div>
      </div>

      {/* Row 4: Invoice date range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>開票日期[從]</span>
          <Input
            type="date"
            value={invDateFrom}
            onChange={(e) => onInvDateFromChange(e.target.value)}
            style={{ width: 114 }}
          />
          <span className="text-sm">[至]</span>
          <Input
            type="date"
            value={invDateTo}
            onChange={(e) => onInvDateToChange(e.target.value)}
            style={{ width: 114 }}
          />
        </div>
      </div>

      {/* Row 5: Payment date range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>繳款日期[從]</span>
          <Input
            type="date"
            value={payDateFrom}
            onChange={(e) => onPayDateFromChange(e.target.value)}
            style={{ width: 114 }}
          />
          <span className="text-sm">[至]</span>
          <Input
            type="date"
            value={payDateTo}
            onChange={(e) => onPayDateToChange(e.target.value)}
            style={{ width: 114 }}
          />
        </div>
      </div>

      {/* Row 6: Payment type range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>繳款類別[從]</span>
          <Select
            value={payTypeFrom}
            onChange={(v) => onPayTypeFromChange(v)}
            options={PAY_TYPE_OPTIONS}
            style={{ width: 140 }}
          />
          <span className="text-sm">[至]</span>
          <Select
            value={payTypeTo}
            onChange={(v) => onPayTypeToChange(v)}
            options={PAY_TYPE_OPTIONS}
            style={{ width: 140 }}
          />
        </div>
      </div>

      {/* Row 7: Document status */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>單據狀態</span>
          <Radio.Group
            value={docStatus}
            onChange={(e: RadioChangeEvent) => onDocStatusChange(e.target.value as number)}
          >
            <Radio value={1}>全部</Radio>
            <Radio value={2}>已核准</Radio>
          </Radio.Group>
        </div>
      </div>

      {/* Row 8: Print selection (report types) */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>印表選擇</span>
          <div className="border border-gray-300 rounded px-3 py-1 flex gap-4">
            <Checkbox
              checked={reportTypes.includes(1)}
              onChange={(e) => handleReportTypeChange(1, e.target.checked)}
            >
              日期別
            </Checkbox>
            <Checkbox
              checked={reportTypes.includes(2)}
              onChange={(e) => handleReportTypeChange(2, e.target.checked)}
            >
              客戶別
            </Checkbox>
            <Checkbox
              checked={reportTypes.includes(3)}
              onChange={(e) => handleReportTypeChange(3, e.target.checked)}
            >
              交運類別
            </Checkbox>
          </div>
        </div>
      </div>

      {/* Query button */}
      <div className="flex gap-2">
        <Button type="primary" icon={<SearchOutlined />} onClick={onQuery} loading={loading}>
          查詢
        </Button>
      </div>
    </div>
  );
};

export default QueryParams;
